import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import { onAppCloseEvent } from "@PublicationReader/constants";
import { ReaderContext } from "@PublicationReader/context";
import {
  useClickOutside,
  useKeyHandler,
  useOnLoadImages,
} from "@PublicationReader/hooks";

import { Overlay } from "@PublicationReader/components/Overlay";
import { Logo } from "@PublicationReader/components/Logo";
import { Meta } from "@PublicationReader/components/Meta";
import { Header } from "@PublicationReader/components/Header";
import { Body } from "@PublicationReader/components/Body";
import { Footer } from "@PublicationReader/components/Footer";
import { ProgressBar } from "@PublicationReader/components/ProgressBar";
import { SkipToButton } from "@PublicationReader/components/SkipToButton";
import { TableOfContents } from "@PublicationReader/components/TableOfContents";
import { DownloadModal } from "@PublicationReader/components/DownloadModal";
import { PublicationI } from "@PublicationReader/@types/Publication";
import { AppProps } from "@PublicationReader/App";

interface ReaderProps extends Pick<AppProps, "apiDownloadUrl"> {
  publication: PublicationI;
}

const Reader = ({ publication, apiDownloadUrl }: ReaderProps) => {
  const { i18n } = useTranslation();

  const { title, subTitle, isEnglish } = publication;
  const { setIsEnglish, setDocumentHeight, documentHeight, tocIsOpen } =
    React.useContext(ReaderContext);
  const ref = React.useRef<HTMLDivElement>(null);
  const readerRef = React.useRef<HTMLDivElement>(null);
  const imagesLoaded = useOnLoadImages(ref as React.RefObject<HTMLDivElement>);

  React.useEffect(() => {
    i18n.changeLanguage(publication.isEnglish ? "en" : "sv");

    document.body.classList.add("pubr--mounted", "pubr--mounted-complete");
    // Set application wide language flag
    setIsEnglish(isEnglish);

    // Set scroll position to top when opening
    window.scrollTo(0, 0);
  }, []);

  React.useLayoutEffect(() => {
    setDocumentHeight(ref?.current?.offsetHeight || 0);
    if (imagesLoaded) {
      setDocumentHeight(ref?.current?.offsetHeight || 0);
    }
  }, [imagesLoaded]);

  // Send close event
  const sendAppCloseEvent = () => {
    const event = new Event(onAppCloseEvent);
    window.dispatchEvent(event);
  };

  // Listeners for closing reader when user clicks outside reader window or hits Esc key
  useClickOutside(readerRef as React.RefObject<HTMLDivElement>, sendAppCloseEvent);
  useKeyHandler("Escape", sendAppCloseEvent);

  return (
    <Overlay ref={ref}>
      <div
        ref={readerRef}
        className={classNames(
          "pubr-reader",
          tocIsOpen && "pubr-reader--toc-open",
        )}
      >
        <DownloadModal
          publication={publication}
          apiDownloadUrl={apiDownloadUrl}
        />

        <TableOfContents {...publication} />

        <div className="pubr-reader__content" lang={isEnglish ? "en" : "sv"}>
          <Header
            publication={publication}
            closeHandler={sendAppCloseEvent}
            apiDownloadUrl={apiDownloadUrl}
          />

          <main className="pubr-reader__main">
            <Logo />
            <h1 className="pubr-reader__heading">{title}</h1>
            {subTitle && <p className="pubr__subtitle">{subTitle}</p>}

            <Meta {...publication} />

            <Body {...publication} />
          </main>

          <SkipToButton />

          <Footer>
            {documentHeight > 0 && <ProgressBar {...publication} />}
          </Footer>
        </div>
      </div>
    </Overlay>
  );
};

export { Reader };
